/**
 * v6.1.17.7 N.2 Team-UI — Web OAuth callback handler.
 *
 * Mounted at `/auth/oauth/:provider/callback`.  The provider redirects
 * here after the user authenticates on their consent screen.
 *
 * Flow (implicit/PKCE — no client secret on the web side):
 *   1. Provider redirects with `id_token` in the URL fragment (Google
 *      response_type=id_token) OR `code` in the query string (Apple
 *      response_mode=form_post sends as POST body — handled separately
 *      via a backend bounce).
 *   2. We extract the token + the `state` query param.
 *   3. CSRF guard: state MUST match the value `LinkedAccountsPanel`
 *      stored in sessionStorage when the flow started.
 *   4. POST the id_token to `/api/auth/oauth/<provider>`.
 *   5. Handle 3 response shapes:
 *        - success (cookie session) → navigate to /home; if
 *          `requires_username: true` the global picker fires.
 *        - link_required → render link-confirm prompt with the
 *          confirmation_token; user submits their existing password
 *          → POST to /api/auth/oauth/link/confirm → navigate /home.
 *        - error → toast + back to /auth.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const SUPPORTED_PROVIDERS = new Set(['google', 'apple']);

function parseHashFragment(hash) {
  const out = {};
  if (!hash) return out;
  const s = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const pair of s.split('&')) {
    const [k, v] = pair.split('=');
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v || '');
  }
  return out;
}

export default function OAuthCallback() {
  const navigate = useNavigate();
  const { provider } = useParams();
  const { refreshUser } = useAuth();
  const [phase, setPhase] = useState('verifying');  // verifying | link_required | error | done
  const [linkConfirmation, setLinkConfirmation] = useState(null);
  const [verifiedLinkAvailable, setVerifiedLinkAvailable] = useState(false);  // 2026-09-17
  const [linkPassword, setLinkPassword] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!SUPPORTED_PROVIDERS.has(provider)) {
      setPhase('error');
      setErrorMsg(`Unsupported OAuth provider: ${provider}`);
      return;
    }
    (async () => {
      try {
        const hashParams = parseHashFragment(window.location.hash);
        const queryParams = new URLSearchParams(window.location.search);
        const idToken = hashParams.id_token || queryParams.get('id_token');
        const state = hashParams.state || queryParams.get('state');
        const expected = sessionStorage.getItem(`fws_oauth_state_${provider}`);
        if (!state || !expected || state !== expected) {
          throw new Error('OAuth state mismatch — possible CSRF.  Please try again.');
        }
        sessionStorage.removeItem(`fws_oauth_state_${provider}`);
        if (!idToken) {
          throw new Error('No id_token in callback URL.');
        }
        // 2026-08 compliance audit — relay the age-gate attestation so a
        // brand-new OAuth account carries a real, timestamped 18+ record.
        let ageAttested = false;
        try { ageAttested = localStorage.getItem('fws_age_confirmed') === 'true'; } catch { /* noop */ }
        const resp = await api.post(`/auth/oauth/${provider}`, { id_token: idToken, age_confirmed: ageAttested });
        const body = resp.data || {};
        if (body.link_required) {
          setLinkConfirmation(body.confirmation_token);
          setVerifiedLinkAvailable(!!body.verified_link_available);
          setPhase('link_required');
          return;
        }
        if (body.mfa_required) {
          // 2026-08-13 audit SEC-001 — MFA accounts can't complete via this
          // redirect callback; send them to the main sign-in which renders
          // the code step. No session was issued.
          setPhase('error');
          setErrorMsg('This account has two-factor enabled. Please sign in from the main login screen to enter your code.');
          return;
        }
        // Plain success — cookie is set server-side. Hydrate AuthContext
        // BEFORE navigating so protected routes see the authenticated user
        // (2026-08-13 audit — a bare navigate('/') left `user` null in memory,
        // bouncing the user back to /auth in a redirect loop until reload).
        await refreshUser();
        setPhase('done');
        toast.success('Signed in!');
        navigate('/');
      } catch (e) {
        setPhase('error');
        // Surface the backend's specific detail (EMAIL_EXISTS_NO_AUTOLINK,
        // audience mismatch, rate-limit…) instead of Axios' generic
        // "Request failed with status code NNN" (final-audit P2).
        const d = e.response?.data?.detail;
        setErrorMsg(
          (typeof d === 'string' ? d : d?.message || d?.code) ||
          e.message || 'OAuth callback failed.'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const submitLinkConfirm = async () => {
    if (!linkPassword || !linkConfirmation) return;
    setLinkSubmitting(true);
    try {
      await api.post('/auth/oauth/link/confirm', {
        confirmation_token: linkConfirmation,
        password: linkPassword,
      });
      await refreshUser();
      toast.success('Linked!');
      navigate('/');
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === 'LINK_PASSWORD_INCORRECT') {
        toast.error('Wrong password — try again.');
      } else if (code === 'LINK_TOKEN_INVALID_OR_EXPIRED') {
        toast.error('Link request expired — please retry the sign-in.');
        navigate('/auth');
      } else {
        toast.error('Could not link — try again.');
      }
    } finally {
      setLinkSubmitting(false);
    }
  };

  // 2026-09-17 — provider-hosted mailbox: link WITHOUT the password (the
  // server disables the old one and revokes other sessions).
  const submitVerifiedLink = async () => {
    if (!linkConfirmation) return;
    setLinkSubmitting(true);
    try {
      await api.post('/auth/oauth/link/verified', { confirmation_token: linkConfirmation });
      await refreshUser();
      toast.success('Signed in!');
      navigate('/');
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === 'LINK_TOKEN_INVALID_OR_EXPIRED') {
        toast.error('Link request expired — please retry the sign-in.');
        navigate('/auth');
      } else {
        toast.error(e.response?.data?.detail?.message || 'Could not connect — try again.');
      }
    } finally {
      setLinkSubmitting(false);
    }
  };

  if (phase === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground" data-testid="oauth-callback-verifying">
          Verifying {provider}…
        </p>
      </div>
    );
  }
  if (phase === 'link_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border/60 rounded-2xl p-6 space-y-4" data-testid="oauth-link-required">
          <h2 className="text-lg font-semibold">Link {provider} to your account</h2>
          <p className="text-sm text-muted-foreground">
            An account with this email already exists.  Enter your existing
            password to link your {provider} login.
          </p>
          {verifiedLinkAvailable && (
            <div className="rounded-xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.08)] p-3 space-y-2" data-testid="oauth-link-verified-offer">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Forgot it? Continue with {provider} instead — your old password will be turned off
                (set a new one anytime via &ldquo;Forgot your password?&rdquo;).
              </p>
              <Button size="sm" variant="outline" className="w-full" onClick={submitVerifiedLink} disabled={linkSubmitting} data-testid="oauth-link-verified-accept">
                {linkSubmitting ? 'Connecting…' : `Continue with ${provider} instead`}
              </Button>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="link-password">Password</Label>
            <Input
              id="link-password"
              type="password"
              value={linkPassword}
              onChange={(e) => setLinkPassword(e.target.value)}
              disabled={linkSubmitting}
              data-testid="oauth-link-password-input"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/auth')}
              data-testid="oauth-link-cancel"
            >Cancel</Button>
            <Button
              onClick={submitLinkConfirm}
              disabled={!linkPassword || linkSubmitting}
              data-testid="oauth-link-confirm"
            >
              {linkSubmitting ? 'Linking…' : 'Link account'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border/60 rounded-2xl p-6 space-y-3" data-testid="oauth-callback-error">
          <h2 className="text-lg font-semibold">Sign-in failed</h2>
          <p className="text-sm text-muted-foreground">{errorMsg}</p>
          <Button onClick={() => navigate('/auth')} data-testid="oauth-callback-error-back">
            Back to sign-in
          </Button>
        </div>
      </div>
    );
  }
  return null;
}
